import styled, { css } from 'styled-components';
import { palette, key } from 'styled-theme';

import { media } from '../../../helper/styleHelper';

const fontSettings = {
  small: {
    size: key('sizes.font.tiny', '9px'),
    spacing: '0.3px',
  },
  normal: {
    size: key('sizes.font.small', '10px'),
    spacing: '0.3px',
  },
  medium: { size: key('sizes.font.medium', '14px'), spacing: '0.3px' },
  big: {
    size: key('sizes.font.normal', '16px'),
    spacing: '0px',
  },
};

const xlrFontSettings = {
  small: {
    size: key('sizes.xlrFont.tiny', '12px'),
    spacing: '0.3px',
  },
  normal: {
    size: key('sizes.xlrFont.normal', '14px'),
    spacing: '0.42px',
  },
  medium: { size: key('sizes.font.medium', '14px'), spacing: '0.3px' },
  big: {
    size: key('sizes.xlrFont.normal', '16px'),
    spacing: '0px',
  },
};

const xxlFontSettings = {
  small: {
    size: key('sizes.xxlFont.tiny', '11px'),
    spacing: '0.3px',
  },
  normal: {
    size: key('sizes.xxlFont.normal', '16px'),
    spacing: '0.42px',
  },
  medium: { size: key('sizes.font.medium', '14px'), spacing: '0.3px' },
  big: {
    size: key('sizes.xxlFont.normal', '16px'),
    spacing: '0px',
  },
};

const vxxlFontSettings = {
  small: {
    size: key('sizes.vxxlFont.tiny', '14px'),
    spacing: '0.3px',
  },
  normal: {
    size: key('sizes.vxxlFont.small', '16px'),
    spacing: '0.42px',
  },
  medium: { size: key('sizes.font.medium', '14px'), spacing: '0.3px' },
  big: {
    size: key('sizes.vxxlFont.normal', '16px'),
    spacing: '0px',
  },
};

// Size settings - matching the provided styles
const sizes = {
  small: '30px',
  normal: '40px',
  medium: '48px',
  big: '48px',
};

const xlrSizes = {
  small: '40px',
  normal: '40px',
  medium: '48px',
  big: '48px',
};

const xxlSizes = {
  small: '40px',
  normal: '50px',
  medium: '60px',
  big: '60px',
};

const vxxlSizes = {
  small: '40px',
  normal: '50px',
  medium: '60px',
  big: '60px',
};

const colors = {
  error: '#FA8072',
};

// Styled Components for Select
export const StyledSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  font-family: JetBrains Mono Regular;
  color: ${palette('text', 1)};
  font-size: ${(props) => fontSettings[props.sizevalue || 'normal'].size};
  letter-spacing: ${(props) => fontSettings[props.sizevalue || 'normal'].spacing};

  ${(props) => media.xlr`
    font-size: ${xlrFontSettings[props.sizevalue || 'normal'].size};
    letter-spacing: ${xlrFontSettings[props.sizevalue || 'normal'].spacing};
  `}

  ${(props) => media.xxl`
    font-size: ${xxlFontSettings[props.sizevalue || 'normal'].size};
    letter-spacing: ${xxlFontSettings[props.sizevalue || 'normal'].spacing};
  `}

  ${(props) => media.vxxl`
    font-size: ${vxxlFontSettings[props.sizevalue || 'normal'].size};
    letter-spacing: ${vxxlFontSettings[props.sizevalue || 'normal'].spacing};
  `}
`;

export const StyledSelectButton = styled.button`
  outline: none;
  box-sizing: border-box;
  font-family: JetBrains Mono Regular;
  padding: 12px 16px;
  border: 1px solid ${palette('border', 0)};
  border-radius: ${key('sizes.borderRadius.tiny', '4px')};
  background-color: ${palette('background', 2)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  text-align: left;
  color: ${palette('text', 1)};

  ${(props) => css`
    height: ${sizes[props.sizevalue]};
    font-size: ${fontSettings[props.sizevalue].size};
    letter-spacing: ${fontSettings[props.sizevalue].spacing};
  `}

  ${(props) => media.sm`
    height: ${sizes[props.sizevalue]};
    padding: 11px 10px;
  `}

  ${(props) => media.xlr`
    height: ${xlrSizes[props.sizevalue]};
    font-size: ${xlrFontSettings[props.sizevalue].size};
    letter-spacing: ${xlrFontSettings[props.sizevalue].spacing};
    padding: 11px 10px;
  `}

  ${(props) => media.xxl`
    height: ${xxlSizes[props.sizevalue]};
    font-size: ${xxlFontSettings[props.sizevalue].size};
    letter-spacing: ${xxlFontSettings[props.sizevalue].spacing};
  `}

  ${(props) => media.vxxl`
    height: ${vxxlSizes[props.sizevalue]};
    font-size: ${vxxlFontSettings[props.sizevalue].size};
    letter-spacing: ${vxxlFontSettings[props.sizevalue].spacing};
  `}

  ${({ readOnly }) =>
    readOnly &&
    css`
      background: #e8e8eb;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: grey;
      &:hover,
      &:focus {
        border-color: #e8e8eb;
        box-shadow: #e8e8eb;
      }
      cursor: not-allowed;
    `}

  ${(props) =>
    props.color === 'error' &&
    css`
      border-color: ${colors[props.color]};
    `}
    
  ${(props) =>
    props.isOpen &&
    css`
      border-color: ${palette('primary', 0)};
      box-shadow: 0 0 0 1px ${palette('primary', 0)};
    `}
    
  svg {
    transition: transform 0.2s ease;
    transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

export const StyledOptionsWrapper = styled.div`
  position: absolute;
  z-index: 10;
  width: 100%;
  margin-top: 4px;
  background-color: ${palette('background', 2)};
  border: 1px solid ${palette('border', 0)};
  border-radius: ${key('sizes.borderRadius.tiny', '4px')};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
`;

export const StyledOptionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const StyledOption = styled.li`
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: JetBrains Mono Regular;
  transition: background-color 0.2s;

  ${(props) => css`
    font-size: ${fontSettings[props.sizevalue].size};
    letter-spacing: ${fontSettings[props.sizevalue].spacing};
  `}

  ${(props) => media.xlr`
    font-size: ${xlrFontSettings[props.sizevalue].size};
    letter-spacing: ${xlrFontSettings[props.sizevalue].spacing};
  `}

  ${(props) => media.xxl`
    font-size: ${xxlFontSettings[props.sizevalue].size};
    letter-spacing: ${xxlFontSettings[props.sizevalue].spacing};
  `}

  ${(props) => media.vxxl`
    font-size: ${vxxlFontSettings[props.sizevalue].size};
    letter-spacing: ${vxxlFontSettings[props.sizevalue].spacing};
  `}

  color: ${palette('text', 1)};
  background-color: ${(props) => (props.isSelected ? palette('background', 3) : 'transparent')};

  &:hover,
  &:focus {
    outline: none;
    background-color: ${palette('background', 3)};
  }

  &.focused {
    background-color: ${palette('background', 3)};
  }
`;

export const ErrorText = styled.div`
  font-family: JetBrains Mono Regular;
  color: ${colors.error};
  font-size: ${fontSettings.small.size};
  margin-top: 4px;
`;
